/**
 * RTK Query API for the staging gate (docs/operations.md, Staging gate).
 *
 * On staging the app renders nothing behind its router until `status`
 * says the gate is open; until then the tab goes to staging's sign-in
 * page, which a production admin's passkey opens (#1319 "Staging gate:
 * sign in with a production admin passkey"). The cookie the backend sets
 * is the whole session, so the endpoints carry no token: a signout just
 * invalidates the status read.
 */
import { createApi } from "@reduxjs/toolkit/query/react";
import {
  STAGING_GATE_ROUTES,
  StagingGateSessionEnvelopeSchema,
  StagingGateSessionSchema,
  unwrapStagingGateSession,
  type StagingGateSession,
} from "@gearghost/api-schemas/stagingGate";
import {
  MessageOnlyEnvelopeSchema,
  type MessageOnlyEnvelope as MessageOnly,
} from "@gearghost/api-schemas/auth";
import axiosBaseQuery from "./axiosBaseQuery";
import { onSchemaFailure } from "./onSchemaFailure";

export const stagingGateApi = createApi({
  reducerPath: "stagingGateApi",
  baseQuery: axiosBaseQuery(),
  onSchemaFailure,
  tagTypes: ["StagingGate"],
  endpoints: (builder) => ({
    // Who opened the gate in this browser; a 401 with the gate message
    // means nobody has, and the tab goes to the sign-in page.
    stagingGateStatus: builder.query<StagingGateSession, void>({
      query: () => STAGING_GATE_ROUTES.status,
      rawResponseSchema: StagingGateSessionEnvelopeSchema,
      transformResponse: unwrapStagingGateSession,
      responseSchema: StagingGateSessionSchema,
      providesTags: ["StagingGate"],
    }),
    stagingGateSignout: builder.mutation<MessageOnly, void>({
      query: () => ({
        url: STAGING_GATE_ROUTES.signout,
        method: "POST",
      }),
      rawResponseSchema: MessageOnlyEnvelopeSchema,
      responseSchema: MessageOnlyEnvelopeSchema,
      invalidatesTags: ["StagingGate"],
    }),
  }),
});

export const {
  useStagingGateStatusQuery,
  useStagingGateSignoutMutation,
} = stagingGateApi;
